import { Link } from 'react-router';
import { useContactModal } from '../hooks/useContactModal';
import { useSupportModal } from '../hooks/useSupportModal';

const footerLinks: Record<string, { label: string; href: string; isExternal?: boolean }[]> = {
  Product: [
    { label: 'Features', href: '/#features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'ROI Calculator', href: '/pricing' },
    { label: 'Coverage Map', href: '/coverage' },
    { label: 'Methodology', href: '/about' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ],
  Resources: [
    { label: 'FAQ', href: '/#faq' },
    { label: 'Documentation', href: '/about' },
    { label: 'Support Center', href: '/support' },
    { label: 'Accessibility', href: '/accessibility' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'DMCA Policy', href: '/legal-compliance' },
  ],
};

function FooterLink({ href, children, className, style }: { href: string; children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const { open } = useContactModal();
  const { open: openSupport } = useSupportModal();

  // Handle anchor-only links (same-page scroll)
  if (href.startsWith('#')) {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (href === '#') { open(); return; }
      if (href === '#support') { openSupport(); return; }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    };
    return <a href={href} onClick={handleClick} className={className} style={style}>{children}</a>;
  }

  // Handle hash links on home page (/features, /faq)
  if (href === '/#features' || href === '/#faq') {
    return (
      <Link to="/" onClick={() => {
        setTimeout(() => {
          const el = document.querySelector(href.replace('/', ''));
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }} className={className} style={style}>
        {children}
      </Link>
    );
  }

  // Internal route — use Link
  return <Link to={href} className={className} style={style}>{children}</Link>;
}

export default function Footer() {
  return (
    <footer id="footer" style={{ background: '#0F1923', padding: '64px 0 32px', borderTop: '1px solid rgba(247,248,244,0.08)' }}>
      <div className="mx-auto max-w-[1280px] px-6">
        {/* Data Accuracy Disclaimer */}
        <div className="mb-10 rounded-xl border p-5" style={{ background: 'rgba(245,245,243,0.03)', borderColor: 'rgba(247,248,244,0.08)' }}>
          <p className="text-[11px] leading-relaxed" style={{ color: '#8A9BAE' }}>
            <strong style={{ color: '#A8B8AE' }}>Disclaimer:</strong> Parcel Lead Pro LLC provides data and analytical tools to assist with land research. Scores and insights are informational only and should not be considered financial, investment, legal, or tax advice. All property data should be independently verified with the appropriate county assessor, recorder, or planning department before making any real estate transaction decisions. Parcel Lead Pro LLC is a technology company, not a licensed real estate broker. See our <Link to="/terms" className="underline" style={{ color: '#A8B8AE' }}>Terms of Service</Link> for full disclaimers.
          </p>
        </div>

        {/* Top row */}
        <div className="flex flex-col justify-between gap-10 md:flex-row">
          <div>
            <Link to="/" className="font-body text-lg font-medium uppercase tracking-[2px]" style={{ color: '#F7F8F4' }}>
              Parcel Lead Pro&trade;
            </Link>
            <p className="mt-1 text-[10px] tracking-wide" style={{ color: '#8A9BAE' }}>
              Data-Powered Land Intelligence
            </p>
            <p className="font-body mt-2 max-w-[320px] text-sm" style={{ color: '#A8B8AE', lineHeight: 1.5 }}>
              Land parcel intelligence for real estate professionals. Starting with deep coverage in 3 South Carolina counties, with expansion planned across NC, TX, VA, and GA.
            </p>
            <p className="font-body mt-2 text-sm" style={{ color: '#A8B8AE' }}>
              <a href="mailto:support@parcleadpro.com" className="transition-colors hover:text-[#F7F8F4]">support@parcleadpro.com</a>
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ background: 'rgba(59,130,246,0.15)', color: '#F7F8F4' }}>CCPA Rights</span>
              <span className="rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ background: 'rgba(59,130,246,0.15)', color: '#F7F8F4' }}>GDPR Rights</span>
              <span className="rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ background: 'rgba(247,248,244,0.08)', color: '#F7F8F4' }}>Registered SC LLC</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 md:gap-16">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h3 className="label-accent mb-4 block text-xs font-bold uppercase tracking-wider" style={{ color: '#F7F8F4' }}>{category}</h3>
                <ul className="flex flex-col gap-2">
                  {links.map((link) => (
                    <li key={link.label}>
                      <FooterLink href={link.href}
                        className="font-body transition-colors duration-200 hover:text-[#F7F8F4]"
                        style={{ fontSize: 13, color: '#A8B8AE' }}>
                        {link.label}
                      </FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t pt-6 md:flex-row" style={{ borderColor: 'rgba(247,248,244,0.12)' }}>
          <div className="text-center md:text-left">
            <p className="font-body text-xs font-bold" style={{ color: '#A8B8AE' }}>
              Parcel Lead Pro LLC
            </p>
            <p className="font-body" style={{ fontSize: 11, color: '#8A9BAE' }}>
              Registered in South Carolina, USA &nbsp;&middot;&nbsp; support@parcleadpro.com
            </p>
            <p className="font-body mt-1" style={{ fontSize: 12, color: '#A8B8AE' }}>
              &copy; 2026 Parcel Lead Pro LLC. All rights reserved.
            </p>
            <p className="font-body mt-0.5" style={{ fontSize: 9, color: '#8A9BAE' }}>
              Kestovar&trade;, BuildSignal&trade;, and Parcel Lead Pro&trade; are trademarks of Parcel Lead Pro LLC.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/privacy" className="font-body transition-colors duration-200 hover:text-[#F7F8F4]" style={{ fontSize: 12, color: '#A8B8AE' }}>Privacy</Link>
            <Link to="/terms" className="font-body transition-colors duration-200 hover:text-[#F7F8F4]" style={{ fontSize: 12, color: '#A8B8AE' }}>Terms</Link>
            <Link to="/legal-compliance" className="font-body transition-colors duration-200 hover:text-[#F7F8F4]" style={{ fontSize: 12, color: '#A8B8AE' }}>Compliance</Link>
            <Link to="/accessibility" className="font-body transition-colors duration-200 hover:text-[#F7F8F4]" style={{ fontSize: 12, color: '#A8B8AE' }}>Accessibility</Link>
            <Link to="/contact" className="font-body transition-colors duration-200 hover:text-[#F7F8F4]" style={{ fontSize: 12, color: '#A8B8AE' }}>Contact</Link>
          </div>
          <p className="font-body text-center md:text-right" style={{ fontSize: 11, color: '#8A9BAE', maxWidth: 300 }}>
            Not a licensed real estate broker. Data is for informational purposes only.
          </p>
        </div>
      </div>
    </footer>
  );
}
